<template>
  <main class="obs-overlay-page" v-loading="loading">
    <el-alert
      class="live-warning"
      title="发布内容会按下方请求间隔同步到所有正在运行的 OBS 页面。"
      type="warning"
      :closable="false"
      show-icon
    ></el-alert>

    <el-card class="runtime-card" shadow="never">
      <div slot="header" class="section-heading runtime-heading">
        <div>
          <h2>直播请求控制</h2>
          <p>关闭后，OBS 页面收到停用状态便会隐藏画面并停止所有后续请求。</p>
        </div>
        <el-tag :type="published.runtime.enabled ? 'success' : 'info'" effect="dark">
          {{ published.runtime.enabled ? '直播请求已开启' : '直播请求已停止' }}
        </el-tag>
      </div>

      <div class="runtime-controls">
        <div class="runtime-field">
          <span class="field-label">请求开关</span>
          <el-switch
            v-model="draft.runtime.enabled"
            active-text="开启"
            inactive-text="关闭"
            aria-label="OBS 直播请求开关"
          ></el-switch>
        </div>
        <div class="runtime-field interval-field">
          <label class="field-label" for="poll-interval-input">请求间隔</label>
          <div class="interval-control">
            <el-input-number
              id="poll-interval-input"
              v-model="draft.runtime.pollIntervalSeconds"
              :min="1"
              :max="60"
              :step="1"
              controls-position="right"
              aria-label="OBS 请求间隔秒数"
            ></el-input-number>
            <span class="interval-unit">秒</span>
          </div>
        </div>
        <el-button
          type="primary"
          class="runtime-save"
          :loading="publishing.runtime"
          :disabled="!runtimeDirty"
          @click="saveRuntime"
        >保存运行设置</el-button>
      </div>

      <p class="runtime-note">
        从关闭改为开启后，需要在 OBS 中刷新一次浏览器源；彻底停止请求的页面无法主动获知重新开启状态。
      </p>
    </el-card>

    <section class="control-grid" aria-label="OBS 文字控制">
      <el-card class="control-card" shadow="never">
        <div slot="header" class="card-header">
          <div>
            <h2>当前进度</h2>
            <p>适合显示正在进行的组别或比赛环节</p>
          </div>
          <el-switch
            v-model="draft.currentProgress.visible"
            active-text="显示"
            inactive-text="隐藏"
            aria-label="当前进度显示状态"
          ></el-switch>
        </div>

        <label class="field-label" for="current-progress-input">画面文字</label>
        <el-input
          id="current-progress-input"
          v-model="draft.currentProgress.text"
          maxlength="120"
          show-word-limit
          clearable
          placeholder="例如：公开组女子 Fit Model E 组"
          @keyup.enter.native="publishCurrentProgress"
        ></el-input>

        <div class="published-meta">
          <span>已发布版本 v{{ published.currentProgress.revision }}</span>
          <span>{{ formatDateTime(published.currentProgress.publishedAt) }}</span>
        </div>

        <div class="card-actions">
          <el-button :disabled="!currentProgressDirty || publishing.currentProgress" @click="restoreCurrentProgress">
            恢复已发布内容
          </el-button>
          <el-button
            type="primary"
            :loading="publishing.currentProgress"
            :disabled="!currentProgressDirty"
            @click="publishCurrentProgress"
          >发布当前进度</el-button>
        </div>
      </el-card>

      <el-card class="control-card" shadow="never">
        <div slot="header" class="card-header">
          <div>
            <h2>滚动字幕</h2>
            <p>每行是一条独立播报，画面会循环滚动</p>
          </div>
          <el-switch
            v-model="draft.scrollingTicker.visible"
            active-text="显示"
            inactive-text="隐藏"
            aria-label="滚动字幕显示状态"
          ></el-switch>
        </div>

        <label class="field-label" for="scrolling-ticker-input">播报内容</label>
        <el-input
          id="scrolling-ticker-input"
          v-model="draft.scrollingTicker.text"
          type="textarea"
          :rows="7"
          maxlength="4000"
          show-word-limit
          placeholder="一行一条播报内容"
        ></el-input>

        <div class="published-meta">
          <span>{{ tickerLineCount }} 条草稿 · 已发布版本 v{{ published.scrollingTicker.revision }}</span>
          <span>{{ formatDateTime(published.scrollingTicker.publishedAt) }}</span>
        </div>

        <div class="card-actions">
          <el-button :disabled="!scrollingTickerDirty || publishing.scrollingTicker" @click="restoreScrollingTicker">
            恢复已发布内容
          </el-button>
          <el-button
            type="primary"
            :loading="publishing.scrollingTicker"
            :disabled="!scrollingTickerDirty"
            @click="publishScrollingTicker"
          >发布滚动字幕</el-button>
        </div>
      </el-card>
    </section>

    <el-card class="url-card" shadow="never">
      <div slot="header" class="section-heading">
        <div>
          <h2>OBS 浏览器源地址</h2>
          <p>在 OBS 中添加“浏览器”来源并粘贴对应地址</p>
        </div>
        <el-button type="danger" plain icon="el-icon-refresh" @click="rotateReadKey">更换只读密钥</el-button>
      </div>

      <div class="url-list">
        <div class="url-item">
          <label for="current-progress-url">当前进度 · Glass v1</label>
          <el-input id="current-progress-url" :value="overlayUrls.currentProgress" readonly>
            <copy-text slot="append" :value="overlayUrls.currentProgress" icon-only></copy-text>
          </el-input>
        </div>
        <div class="url-item">
          <label for="scrolling-ticker-url">滚动字幕 · Glass v1</label>
          <el-input id="scrolling-ticker-url" :value="overlayUrls.scrollingTicker" readonly>
            <copy-text slot="append" :value="overlayUrls.scrollingTicker" icon-only></copy-text>
          </el-input>
        </div>
      </div>
    </el-card>

    <el-card class="history-card" shadow="never">
      <div slot="header" class="section-heading">
        <div>
          <h2>最近发布记录</h2>
          <p>最多保留 50 条，回退也会生成新的发布记录</p>
        </div>
        <el-button icon="el-icon-refresh" :loading="loading" @click="loadConfig">刷新</el-button>
      </div>

      <div v-if="history.length" class="history-columns">
        <section class="history-column" aria-labelledby="current-progress-history-title">
          <div class="history-column-header">
            <div>
              <h3 id="current-progress-history-title">当前进度</h3>
              <p>共 {{ currentProgressHistory.length }} 条记录</p>
            </div>
            <el-tag size="small" effect="plain">{{ currentProgressHistory.length }}</el-tag>
          </div>

          <div v-if="paginatedCurrentProgressHistory.length" class="history-list">
            <article
              v-for="item in paginatedCurrentProgressHistory"
              :key="item.historyId"
              class="history-item"
            >
              <div class="history-main">
                <div class="history-title-row">
                  <strong>v{{ item.revision }}</strong>
                  <el-tag size="mini" :type="item.value && item.value.visible ? 'success' : 'info'">
                    {{ item.value && item.value.visible ? '显示' : '隐藏' }}
                  </el-tag>
                  <el-tag v-if="item.source === 'rollback'" size="mini" type="warning">回退发布</el-tag>
                </div>
                <p class="history-content">{{ historySummary(item) }}</p>
                <div class="history-meta">
                  <span>{{ publisherName(item) }}</span>
                  <span>{{ formatDateTime(item.publishedAt) }}</span>
                </div>
              </div>
              <el-button type="text" class="rollback-button" @click="rollback(item)">回退到此版本</el-button>
            </article>
          </div>
          <el-empty v-else :image-size="72" description="暂无当前进度记录"></el-empty>

          <el-pagination
            v-if="currentProgressHistory.length > historyPageSize"
            class="history-pagination"
            small
            background
            layout="prev, pager, next"
            :total="currentProgressHistory.length"
            :page-size="historyPageSize"
            :current-page.sync="historyPages.currentProgress"
          ></el-pagination>
        </section>

        <section class="history-column" aria-labelledby="scrolling-ticker-history-title">
          <div class="history-column-header">
            <div>
              <h3 id="scrolling-ticker-history-title">滚动字幕</h3>
              <p>共 {{ scrollingTickerHistory.length }} 条记录</p>
            </div>
            <el-tag size="small" effect="plain">{{ scrollingTickerHistory.length }}</el-tag>
          </div>

          <div v-if="paginatedScrollingTickerHistory.length" class="history-list">
            <article
              v-for="item in paginatedScrollingTickerHistory"
              :key="item.historyId"
              class="history-item"
            >
              <div class="history-main">
                <div class="history-title-row">
                  <strong>v{{ item.revision }}</strong>
                  <el-tag size="mini" :type="item.value && item.value.visible ? 'success' : 'info'">
                    {{ item.value && item.value.visible ? '显示' : '隐藏' }}
                  </el-tag>
                  <el-tag v-if="item.source === 'rollback'" size="mini" type="warning">回退发布</el-tag>
                </div>
                <p class="history-content">{{ historySummary(item) }}</p>
                <div class="history-meta">
                  <span>{{ publisherName(item) }}</span>
                  <span>{{ formatDateTime(item.publishedAt) }}</span>
                </div>
              </div>
              <el-button type="text" class="rollback-button" @click="rollback(item)">回退到此版本</el-button>
            </article>
          </div>
          <el-empty v-else :image-size="72" description="暂无滚动字幕记录"></el-empty>

          <el-pagination
            v-if="scrollingTickerHistory.length > historyPageSize"
            class="history-pagination"
            small
            background
            layout="prev, pager, next"
            :total="scrollingTickerHistory.length"
            :page-size="historyPageSize"
            :current-page.sync="historyPages.scrollingTicker"
          ></el-pagination>
        </section>
      </div>
      <el-empty v-else description="暂无发布记录"></el-empty>
    </el-card>
  </main>
</template>

<script>
const emptyProgress = () => ({ text: '', visible: false, revision: 0, publishedAt: null });
const emptyTicker = () => ({ items: [], visible: false, revision: 0, publishedAt: null });
const emptyRuntime = () => ({ enabled: false, pollIntervalMs: 1000, revision: 0, updatedAt: null });

export default {
  name: 'ObsOverlayManage',
  data() {
    return {
      loading: false,
      publishing: {
        currentProgress: false,
        scrollingTicker: false,
        runtime: false
      },
      published: {
        currentProgress: emptyProgress(),
        scrollingTicker: emptyTicker(),
        runtime: emptyRuntime()
      },
      draft: {
        currentProgress: { text: '', visible: false },
        scrollingTicker: { text: '', visible: false },
        runtime: { enabled: false, pollIntervalSeconds: 1 }
      },
      overlayUrls: {
        currentProgress: '',
        scrollingTicker: ''
      },
      history: [],
      historyPageSize: 5,
      historyPages: {
        currentProgress: 1,
        scrollingTicker: 1
      }
    };
  },
  computed: {
    runtimeDirty() {
      return this.draft.runtime.enabled !== !!this.published.runtime.enabled ||
        this.draft.runtime.pollIntervalSeconds * 1000 !== Number(this.published.runtime.pollIntervalMs || 1000);
    },
    currentProgressDirty() {
      return this.draft.currentProgress.text !== (this.published.currentProgress.text || '') ||
        this.draft.currentProgress.visible !== !!this.published.currentProgress.visible;
    },
    scrollingTickerItems() {
      return this.draft.scrollingTicker.text
        .replace(/\r/g, '')
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean);
    },
    scrollingTickerDirty() {
      const publishedText = (this.published.scrollingTicker.items || []).join('\n');
      return this.scrollingTickerItems.join('\n') !== publishedText ||
        this.draft.scrollingTicker.visible !== !!this.published.scrollingTicker.visible;
    },
    tickerLineCount() {
      return this.scrollingTickerItems.length;
    },
    currentProgressHistory() {
      return this.history.filter(item => item.field === 'currentProgress');
    },
    scrollingTickerHistory() {
      return this.history.filter(item => item.field === 'scrollingTicker');
    },
    paginatedCurrentProgressHistory() {
      return this.paginateHistory(this.currentProgressHistory, this.historyPages.currentProgress);
    },
    paginatedScrollingTickerHistory() {
      return this.paginateHistory(this.scrollingTickerHistory, this.historyPages.scrollingTicker);
    }
  },
  created() {
    this.loadConfig();
  },
  methods: {
    responseData(response) {
      return response && response.data ? response.data.res : null;
    },
    hydrate(data) {
      if (!data) return;
      this.published.currentProgress = data.currentProgress || emptyProgress();
      this.published.scrollingTicker = data.scrollingTicker || emptyTicker();
      this.published.runtime = data.runtime || emptyRuntime();
      this.draft.currentProgress = {
        text: this.published.currentProgress.text || '',
        visible: !!this.published.currentProgress.visible
      };
      this.draft.scrollingTicker = {
        text: (this.published.scrollingTicker.items || []).join('\n'),
        visible: !!this.published.scrollingTicker.visible
      };
      this.draft.runtime = {
        enabled: !!this.published.runtime.enabled,
        pollIntervalSeconds: Math.round(Number(this.published.runtime.pollIntervalMs || 1000) / 1000)
      };
      this.overlayUrls = data.overlayUrls || { currentProgress: '', scrollingTicker: '' };
      this.history = data.history || [];
      this.historyPages.currentProgress = 1;
      this.historyPages.scrollingTicker = 1;
    },
    async loadConfig() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/obs-overlay/config');
        this.hydrate(this.responseData(response));
      } catch (error) {
        this.showError(error, '加载 OBS 配置失败');
      } finally {
        this.loading = false;
      }
    },
    restoreCurrentProgress() {
      this.draft.currentProgress = {
        text: this.published.currentProgress.text || '',
        visible: !!this.published.currentProgress.visible
      };
    },
    restoreScrollingTicker() {
      this.draft.scrollingTicker = {
        text: (this.published.scrollingTicker.items || []).join('\n'),
        visible: !!this.published.scrollingTicker.visible
      };
    },
    async publishCurrentProgress() {
      if (!this.currentProgressDirty || this.publishing.currentProgress) return;
      this.publishing.currentProgress = true;
      try {
        const response = await this.$axios.post('/obs-overlay/current-progress/publish', {
          text: this.draft.currentProgress.text,
          visible: this.draft.currentProgress.visible,
          expectedRevision: this.published.currentProgress.revision
        });
        this.hydrate(this.responseData(response));
        this.$message.success('当前进度已发布');
      } catch (error) {
        await this.handlePublishError(error);
      } finally {
        this.publishing.currentProgress = false;
      }
    },
    async saveRuntime() {
      if (!this.runtimeDirty || this.publishing.runtime) return;
      const wasEnabled = !!this.published.runtime.enabled;
      this.publishing.runtime = true;
      try {
        const response = await this.$axios.post('/obs-overlay/runtime/update', {
          enabled: this.draft.runtime.enabled,
          pollIntervalMs: this.draft.runtime.pollIntervalSeconds * 1000,
          expectedRevision: this.published.runtime.revision
        });
        this.hydrate(this.responseData(response));
        if (!wasEnabled && this.published.runtime.enabled) {
          this.$message.success('直播请求已开启，请在 OBS 中刷新浏览器源');
        } else if (!this.published.runtime.enabled) {
          this.$message.success('直播请求已关闭，OBS 页面将在下次同步后停止请求');
        } else {
          this.$message.success('请求频率已更新');
        }
      } catch (error) {
        await this.handlePublishError(error);
      } finally {
        this.publishing.runtime = false;
      }
    },
    async publishScrollingTicker() {
      if (!this.scrollingTickerDirty || this.publishing.scrollingTicker) return;
      if (this.scrollingTickerItems.length > 30) {
        this.$message.warning('滚动字幕不能超过 30 行');
        return;
      }
      this.publishing.scrollingTicker = true;
      try {
        const response = await this.$axios.post('/obs-overlay/scrolling-ticker/publish', {
          items: this.scrollingTickerItems,
          visible: this.draft.scrollingTicker.visible,
          expectedRevision: this.published.scrollingTicker.revision
        });
        this.hydrate(this.responseData(response));
        this.$message.success('滚动字幕已发布');
      } catch (error) {
        await this.handlePublishError(error);
      } finally {
        this.publishing.scrollingTicker = false;
      }
    },
    async handlePublishError(error) {
      const code = error && error.response && error.response.data && error.response.data.code;
      if (code === 'OBS_OVERLAY_REVISION_CONFLICT') {
        this.$message.warning('配置已被其他人更新，已为你重新加载');
        await this.loadConfig();
        return;
      }
      this.showError(error, '发布失败');
    },
    async rollback(item) {
      const field = item.field;
      const current = this.published[field];
      try {
        await this.$confirm(
          `确认将${this.fieldLabel(field)}回退并重新发布为 v${item.revision} 的内容吗？`,
          '确认回退',
          { type: 'warning', confirmButtonText: '确认回退', cancelButtonText: '取消' }
        );
        const response = await this.$axios.post('/obs-overlay/rollback', {
          historyId: item.historyId,
          field,
          expectedRevision: current.revision
        });
        this.hydrate(this.responseData(response));
        this.$message.success('历史内容已重新发布');
      } catch (error) {
        if (error === 'cancel' || error === 'close') return;
        await this.handlePublishError(error);
      }
    },
    async rotateReadKey() {
      try {
        await this.$confirm(
          '更换后，当前 OBS 地址会立即失效。请确认你能及时替换所有 OBS 浏览器源地址。',
          '更换只读密钥',
          { type: 'warning', confirmButtonText: '确认更换', cancelButtonText: '取消' }
        );
        const response = await this.$axios.post('/obs-overlay/read-key/rotate');
        this.hydrate(this.responseData(response));
        this.$message.success('只读密钥已更换，请复制新的 OBS 地址');
      } catch (error) {
        if (error === 'cancel' || error === 'close') return;
        this.showError(error, '更换只读密钥失败');
      }
    },
    showError(error, fallback) {
      const message = error && error.response && error.response.data && error.response.data.message;
      this.$message.error(message || fallback);
    },
    formatDateTime(value) {
      return value ? this.$dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '尚未发布';
    },
    fieldLabel(field) {
      return field === 'currentProgress' ? '当前进度' : '滚动字幕';
    },
    historySummary(item) {
      if (!item || !item.value) return '空内容';
      const text = item.field === 'currentProgress'
        ? item.value.text
        : (item.value.items || []).join(' ｜ ');
      return text || '空内容';
    },
    publisherName(item) {
      return item && item.publishedBy && item.publishedBy.username
        ? item.publishedBy.username
        : '未知用户';
    },
    paginateHistory(items, page) {
      const start = (page - 1) * this.historyPageSize;
      return items.slice(start, start + this.historyPageSize);
    }
  }
};
</script>

<style scoped>
.obs-overlay-page {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  color: var(--text-primary);
}

.section-heading,
.card-header,
.history-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

h2,
p {
  margin-top: 0;
}

h2 {
  margin-bottom: 5px;
  font-size: 18px;
  line-height: 1.35;
}

.card-header p,
.section-heading p {
  margin-bottom: 0;
  color: var(--text-regular);
  font-size: 14px;
  line-height: 1.6;
}

.live-warning {
  margin-bottom: 18px;
}

.control-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.runtime-card {
  margin-bottom: 18px;
  border-color: var(--card-border-color);
  background: var(--bg-card);
}

.runtime-heading {
  align-items: center;
}

.runtime-controls {
  display: flex;
  align-items: flex-end;
  gap: 28px;
}

.runtime-field {
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 12px;
}

.runtime-field .field-label {
  margin-bottom: 0;
}

.interval-field {
  min-width: 292px;
}

.interval-control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.interval-control .el-input-number {
  width: 168px;
}

.interval-unit {
  color: var(--text-regular);
  font-size: 14px;
}

.runtime-save {
  min-height: 44px;
  margin-left: auto;
}

.runtime-note {
  margin: 14px 0 0;
  color: var(--color-warning);
  font-size: 13px;
  line-height: 1.6;
}

.control-card,
.url-card,
.history-card {
  border-color: var(--card-border-color);
  background: var(--bg-card);
}

.url-card,
.history-card {
  margin-top: 18px;
}

.field-label,
.url-item label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.published-meta,
.history-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 12px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.card-actions .el-button,
.section-heading .el-button,
.rollback-button {
  min-height: 44px;
}

.url-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.history-list {
  display: grid;
  gap: 10px;
}

.history-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.history-column {
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  background: transparent;
}

.history-column-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.history-column-header h3 {
  margin: 0 0 4px;
  color: var(--text-primary);
  font-size: 16px;
  line-height: 1.4;
}

.history-column-header p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
}

.history-pagination {
  margin-top: 16px;
  text-align: center;
}

.history-item {
  padding: 14px 16px;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  background: transparent;
}

.history-main {
  min-width: 0;
}

.history-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.history-content {
  display: -webkit-box;
  overflow: hidden;
  margin: 8px 0 0;
  color: var(--text-regular);
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.rollback-button {
  flex: 0 0 auto;
}

@media (max-width: 768px) {
  .obs-overlay-page {
    padding: 0;
  }

  .control-grid,
  .url-list,
  .history-columns {
    grid-template-columns: 1fr;
  }

  .runtime-controls {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .runtime-field {
    justify-content: space-between;
  }

  .interval-field {
    min-width: 0;
  }

  .interval-control {
    flex: 0 0 auto;
  }

  .interval-control .el-input-number {
    width: 150px;
  }

  .runtime-save {
    width: 100%;
    margin-left: 0;
  }

  .section-heading,
  .card-header,
  .history-item {
    gap: 12px;
  }

  .section-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .section-heading > .el-tag {
    align-self: flex-start;
  }

  .card-header {
    align-items: center;
  }

  .card-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .card-actions .el-button {
    width: 100%;
    margin-left: 0;
  }

  .section-heading .el-button {
    width: 100%;
  }

  .history-item {
    align-items: stretch;
    flex-direction: column;
  }

  .history-column {
    padding: 12px;
  }

  .rollback-button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .obs-overlay-page * {
    transition-duration: 0.01ms !important;
  }
}
</style>
